import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, delay, map, shareReplay } from 'rxjs';
import {
  User,
  Document,
  Workflow,
  Memo,
  Payment,
  Notification,
  ActivityItem,
  DashboardStats,
  Department,
} from '../models';
import { environment } from '../../../environments/environment';

interface SearchResult {
  type: 'document' | 'memo' | 'user';
  id: string;
  title: string;
  subtitle: string;
}

const BASE_PATH = environment.mockDataPath;

function randomDelay(): number {
  return 200 + Math.floor(Math.random() * 300);
}

@Injectable({ providedIn: 'root' })
export class MockDataService {
  private readonly http = inject(HttpClient);

  // Cached observables
  private users$?: Observable<User[]>;
  private documents$?: Observable<Document[]>;
  private workflows$?: Observable<Workflow[]>;
  private memos$?: Observable<Memo[]>;
  private payments$?: Observable<Payment[]>;
  private notifications$?: Observable<Notification[]>;
  private activityFeed$?: Observable<ActivityItem[]>;
  private dashboardStats$?: Observable<DashboardStats>;
  private departments$?: Observable<Department[]>;

  // ── Core data loaders ─────────────────────────────────────────

  getUsers(): Observable<User[]> {
    this.users$ ??= this.load<User[]>('users');
    return this.users$.pipe(delay(randomDelay()));
  }

  getDocuments(): Observable<Document[]> {
    this.documents$ ??= this.load<Document[]>('documents');
    return this.documents$.pipe(delay(randomDelay()));
  }

  getWorkflows(): Observable<Workflow[]> {
    this.workflows$ ??= this.load<Workflow[]>('workflows');
    return this.workflows$.pipe(delay(randomDelay()));
  }

  getMemos(): Observable<Memo[]> {
    this.memos$ ??= this.load<Memo[]>('memos');
    return this.memos$.pipe(delay(randomDelay()));
  }

  getPayments(): Observable<Payment[]> {
    this.payments$ ??= this.load<Payment[]>('payments');
    return this.payments$.pipe(delay(randomDelay()));
  }

  getNotifications(): Observable<Notification[]> {
    this.notifications$ ??= this.load<Notification[]>('notifications');
    return this.notifications$.pipe(delay(randomDelay()));
  }

  getActivityFeed(): Observable<ActivityItem[]> {
    this.activityFeed$ ??= this.load<ActivityItem[]>('activity');
    return this.activityFeed$.pipe(delay(randomDelay()));
  }

  getDashboardStats(): Observable<DashboardStats> {
    this.dashboardStats$ ??= this.load<DashboardStats>('dashboard-stats');
    return this.dashboardStats$.pipe(delay(randomDelay()));
  }

  getDepartments(): Observable<Department[]> {
    this.departments$ ??= this.load<Department[]>('departments');
    return this.departments$.pipe(delay(randomDelay()));
  }

  // ── Filter / search methods ───────────────────────────────────

  getDocumentsByStatus(status: Document['status']): Observable<Document[]> {
    return this.getDocuments().pipe(
      map(docs => docs.filter(d => d.status === status)),
    );
  }

  getDocumentsByDepartment(dept: string): Observable<Document[]> {
    return this.getDocuments().pipe(
      map(docs => docs.filter(d => d.department === dept)),
    );
  }

  getWorkflowsByStatus(status: Workflow['status']): Observable<Workflow[]> {
    return this.getWorkflows().pipe(
      map(wfs => wfs.filter(w => w.status === status)),
    );
  }

  getUserById(id: string): Observable<User | undefined> {
    return this.getUsers().pipe(
      map(users => users.find(u => u.id === id)),
    );
  }

  getUsersByDepartment(dept: string): Observable<User[]> {
    return this.getUsers().pipe(
      map(users => users.filter(u => u.department === dept)),
    );
  }

  searchAll(query: string): Observable<SearchResult[]> {
    const q = query.toLowerCase().trim();
    if (!q) {
      return of<SearchResult[]>([]).pipe(delay(randomDelay()));
    }

    return new Observable<SearchResult[]>(subscriber => {
      const results: SearchResult[] = [];
      let completed = 0;
      const total = 3;

      const tryComplete = (): void => {
        completed++;
        if (completed === total) {
          subscriber.next(results);
          subscriber.complete();
        }
      };

      this.getDocuments().subscribe({
        next: docs => {
          docs.forEach(d => {
            if (d.title.toLowerCase().includes(q) || d.reference.toLowerCase().includes(q)) {
              results.push({ type: 'document', id: d.id, title: d.title, subtitle: d.reference });
            }
          });
          tryComplete();
        },
        error: err => subscriber.error(err),
      });

      this.getMemos().subscribe({
        next: memos => {
          memos.forEach(m => {
            if (m.subject.toLowerCase().includes(q) || m.body.toLowerCase().includes(q)) {
              results.push({ type: 'memo', id: m.id, title: m.subject, subtitle: m.category });
            }
          });
          tryComplete();
        },
        error: err => subscriber.error(err),
      });

      this.getUsers().subscribe({
        next: users => {
          users.forEach(u => {
            const name = `${u.firstName} ${u.lastName}`.toLowerCase();
            if (name.includes(q) || u.email.toLowerCase().includes(q)) {
              results.push({ type: 'user', id: u.id, title: `${u.firstName} ${u.lastName}`, subtitle: u.title });
            }
          });
          tryComplete();
        },
        error: err => subscriber.error(err),
      });
    });
  }

  // ── Private helpers ───────────────────────────────────────────

  private load<T>(filename: string): Observable<T> {
    return this.http.get<T>(`${BASE_PATH}/${filename}.json`).pipe(
      shareReplay(1),
    );
  }
}
